import { useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { Instagram, Globe, Camera, Images, Link2, Share, BookOpen, ChevronRight, MapPin } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook } from "@fortawesome/free-brands-svg-icons";
import { TikTokLogo, XLogo, RedditLogo } from "@/components/shared/PlatformIcon";
import { AddContentModal } from "@/components/modals/AddContentModal";
import { openSocialUrl, openExternalUrl } from "@/utils/platformUtils";
import { isNativeApp } from "@/utils/platform-detection";
import { SETUP, BASICS, ACCOUNT_SETTINGS } from "@/routes";
import { EXTENSION_LINKS } from "@/constants/links";
import { cn } from "@/lib/utils";
import { CAPTION, CONTROL, CTA, LABEL, META, ROW_TITLE, SECTION_TITLE, SUBTITLE, TITLE } from "@/lib/typography";

type ContentType = "image" | "link" | "note";

const ACTION_ITEMS = [
  { key: "photo", icon: Camera, label: "Photo" },
  { key: "images", icon: Images, label: "Image" },
  { key: "link", icon: Link2, label: "Link" },
] as const;

type ActionKey = typeof ACTION_ITEMS[number]["key"];

type SocialPlatform = "instagram" | "tiktok" | "facebook" | "x" | "reddit";

const SOCIAL_PLATFORMS: { id: SocialPlatform; name: string; url: string; bgStyle: string; iconStyle: string }[] = [
  { id: "instagram", name: "Instagram", url: "https://instagram.com", bgStyle: "bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#F77737]", iconStyle: "text-white" },
  { id: "tiktok", name: "TikTok", url: "https://tiktok.com", bgStyle: "bg-black", iconStyle: "text-white" },
  { id: "facebook", name: "Facebook", url: "https://facebook.com", bgStyle: "bg-[#1877F2]", iconStyle: "text-white" },
  { id: "x", name: "X", url: "https://x.com", bgStyle: "bg-black", iconStyle: "text-white" },
  { id: "reddit", name: "Reddit", url: "https://reddit.com", bgStyle: "bg-[#FF4500]", iconStyle: "text-white" },
];

function getPlatformIcon(platformId: SocialPlatform) {
  switch (platformId) {
    case "instagram":
      return <Instagram className="w-8 h-8" />;
    case "tiktok":
      return <TikTokLogo size={32} />;
    case "facebook":
      return <FontAwesomeIcon icon={faFacebook} size="2x" />;
    case "x":
      return <XLogo size={32} />;
    case "reddit":
      return <RedditLogo size={32} />;
    default:
      return <Globe className="w-8 h-8" />;
  }
}

export interface EmptyStateProps {
  /** Main headline text */
  headline: string;
  /** Descriptive message */
  message: string;
  /** Rendering variant - "full" replaces content, "overlay" shows glass effect over page */
  variant?: "full" | "overlay";
  /** Page-specific icon (shown in overlay mode) */
  icon?: React.ReactNode;
  /** Show platform shortcut buttons (Instagram, TikTok, etc.) */
  showPlatformButtons?: boolean;
  /** Show Photo / Image / Link action buttons */
  showActionButtons?: boolean;
  /** localStorage key for persisting dismissal. If set, shows a dismiss button. */
  dismissKey?: string;
  /** Show a dismiss button without persisting dismissal. */
  dismissible?: boolean;
  /** Show setup/learn cards linking to /setup and /basics */
  showSetupCards?: boolean;
  /** Show "Ways to save" method cards (share, Chrome, paste) */
  showWaysToSave?: boolean;
  /** Optional map discovery prompt shown below the full empty-state save controls */
  mapPrompt?: {
    headline: string;
    ctaLabel: string;
    to: string;
  };
  /** Show a single CTA button linking to /setup (for overlay variants) */
  showSimpleCTA?: boolean;
  /** Additional class names */
  className?: string;
  /** Optional supporting content rendered below the message. */
  children?: React.ReactNode;
}

/**
 * EmptyState - Flexible empty state component for pages with no content
 *
 * Supports two variants:
 * - "full" (default): Replaces page content entirely, used for homepage onboarding
 * - "overlay": Glass morphism overlay on top of page, shows underlying UI
 *
 * Features are configurable via props:
 * - Animated visual (share→dots→squirrel)
 * - Page-specific icon
 * - Platform shortcut buttons
 * - Arrow hint pointing to + button
 */
export function EmptyState({
  headline,
  message,
  variant = "full",
  icon,
  showPlatformButtons = false,
  showActionButtons = false,
  dismissKey,
  dismissible = false,
  showSetupCards = false,
  showWaysToSave = false,
  mapPrompt,
  showSimpleCTA = false,
  className,
  children,
}: EmptyStateProps) {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContentType, setModalContentType] = useState<ContentType>("link");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [activeTab, setActiveTab] = useState<"import" | "new">("new");
  const [saveControlsOpen, setSaveControlsOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (!dismissKey) return false;
    try { return localStorage.getItem(dismissKey) === "true"; } catch { return false; }
  });

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    if (dismissKey) {
      try { localStorage.setItem(dismissKey, "true"); } catch {
        // Ignore localStorage errors.
      }
    }
  }, [dismissKey]);

  const handleActionClick = useCallback(async (key: ActionKey) => {
    if (key === "link") {
      setModalContentType("link");
      setPendingFiles([]);
      setModalOpen(true);
      return;
    }

    if (isNativeApp()) {
      try {
        const { pickFromCamera, pickFromLibrary } = await import("@/utils/nativeMediaPicker");
        const files = key === "photo" ? await pickFromCamera() : await pickFromLibrary();
        if (files.length > 0) {
          setPendingFiles(files);
          setModalContentType("image");
          setModalOpen(true);
        }
      } catch (err) {
        console.log("[EmptyState] Picker cancelled:", err);
      }
      return;
    }

    // Web: open modal in image mode (drag-and-drop)
    setModalContentType("image");
    setPendingFiles([]);
    setModalOpen(true);
  }, []);

  const handlePlatformClick = (platform: typeof SOCIAL_PLATFORMS[number]) => {
    openSocialUrl(platform.url, platform.id);
  };

  const hasSaveOwnDropdown = Boolean(mapPrompt && (showSetupCards || showWaysToSave));
  const showSaveOwnControls = !hasSaveOwnDropdown || saveControlsOpen;

  if (dismissed) return null;

  // Overlay variant - glass morphism effect over page content
  if (variant === "overlay") {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
    className={cn(
          "fixed inset-0 z-[70]",
          "pointer-events-none",
          // Yield to the AddContentModal this card launches — the modal's dialog
          // layer (z-50) sits below this overlay, so the overlay must hide while
          // the modal is open. The modal portals to body, so it stays mounted.
          modalOpen && "invisible",
          "lg:top-[72px]", // Leave room for desktop nav
          " bg-background/70",
          "flex items-center justify-center p-4",
          className
        )}
      >
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.3 }}
     className={cn(
            "w-full max-w-md relative",
            "pointer-events-auto",
            "bg-card/80",
            "",
            "rounded-3xl",
            "border border-border",
            "p-6 sm:p-8",
            "text-center"
          )}
        >
          {/* Headline */}
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
      className={cn(
              TITLE,
              "text-foreground mb-4"
            )}
          >
            {headline}
          </motion.h2>

          {/* Icon */}
          {icon && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
       className="mb-4 flex justify-center"
            >
       <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-100 to-brand-200 dark:from-brand-900/50 dark:to-brand-800/50 flex items-center justify-center">
        <div className="text-brand-accent">
                  {icon}
                </div>
              </div>
            </motion.div>
          )}

          {/* Message */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
      className={cn(
              SUBTITLE,
              "mb-6"
            )}
          >
            {message}
          </motion.p>

          {children && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mb-4 flex justify-center"
            >
              {children}
            </motion.div>
          )}

          {/* Simple CTA (overlay simplified) */}
          {showSimpleCTA && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="mb-4"
            >
              <button
                onClick={() => navigate(SETUP())}
                className={cn(CONTROL, "px-6 py-3 rounded-full bg-brand-accent text-brand-on-accent active:scale-95 transition-transform")}
              >
                Set up sharing
              </button>
            </motion.div>
          )}

          {/* Platform Buttons (optional) */}
          {showPlatformButtons && !showSimpleCTA && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
       className=""
            >
       <div className="flex justify-center gap-2 sm:gap-3 flex-wrap">
                {SOCIAL_PLATFORMS.map((platform, index) => (
                  <motion.button
                    key={platform.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + index * 0.03 }}
                    whileTap={{ scale: 0.95 }}
                    whileHover={{ scale: 1.05 }}
                    onClick={() => handlePlatformClick(platform)}
          className={cn(
                      "w-14 h-14 rounded-xl",
                      "flex items-center justify-center",
                      platform.bgStyle,
                      platform.iconStyle,
                      platform.id === "tiktok" && "dark:bg-foreground dark:text-background",
                      platform.id === "x" && "dark:bg-foreground dark:text-background"
                    )}
                    aria-label={`Open ${platform.name}`}
                  >
                    {getPlatformIcon(platform.id)}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Action Buttons (optional) */}
          {showActionButtons && !showSimpleCTA && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col items-center pt-2"
            >
              <p className={cn(META, "text-center mb-3")}>
                or add directly
              </p>
              <div className="flex justify-center gap-3">
                {ACTION_ITEMS.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.button
                      key={item.key}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.55 + index * 0.05 }}
                      whileTap={{ scale: 0.95 }}
                      whileHover={{ scale: 1.05 }}
                      onClick={() => handleActionClick(item.key)}
                      className="flex flex-col items-center gap-1.5"
                    >
                      <div className="w-12 h-12 rounded-xl bg-card border border-border flex items-center justify-center">
                        <Icon className="w-5 h-5 text-foreground" />
                      </div>
                      <span className={CAPTION}>{item.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Dismiss button */}
          {(dismissKey || dismissible) && (
            <motion.button
              type="button"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              onClick={handleDismiss}
              className={cn(META, "w-full pt-4 mt-2 border-t border-border hover:text-foreground transition-colors")}
            >
              Dismiss
            </motion.button>
          )}
        </motion.div>

        {showActionButtons && (
          <AddContentModal
            isOpen={modalOpen}
            onClose={() => { setModalOpen(false); setPendingFiles([]); }}
            contentType={modalContentType}
            initialFiles={pendingFiles.length > 0 ? pendingFiles : undefined}
          />
        )}
      </motion.div>
    );
  }

  // Full variant - replaces page content entirely
  return (
  <div className={cn(
      "flex flex-col items-center px-4 md:px-6 pb-8 w-full",
      className
    )}>
   <div className="w-full max-w-md mx-auto flex flex-col">
        {/* Main content */}
    <div className="flex flex-col">
          {/* Headline */}
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={cn(TITLE, "text-foreground mb-1 text-center desktop:mt-8")}
          >
            {headline}
          </motion.h2>

          {/* Body message */}
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={cn(SUBTITLE, "max-w-sm mx-auto text-center mb-8")}
          >
            {message}
          </motion.p>

          {/* Icon (for non-animated visual pages like stashes/map/events) */}
          {!showSetupCards && icon && (
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-brand-100 to-brand-200 dark:from-brand-900/50 dark:to-brand-800/50 flex items-center justify-center">
                <div className="text-brand-accent">
                  {icon}
                </div>
              </div>
            </div>
          )}

          {mapPrompt && (showSetupCards || showWaysToSave) && (
            <motion.button
              type="button"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              onClick={() => setSaveControlsOpen(open => !open)}
              aria-expanded={saveControlsOpen}
              className={cn(SECTION_TITLE, "w-full flex items-center justify-center gap-2 border-t border-border pt-10 text-foreground text-center mb-4 active:scale-[0.98] transition-transform")}
            >
              <span>Let's get you set up</span>
              <ChevronRight className={cn("h-5 w-5 transition-transform", saveControlsOpen && "rotate-90")} />
            </motion.button>
          )}

          <AnimatePresence initial={false}>
          {showSaveOwnControls && (
            <motion.div
              key="save-own-controls"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="w-full overflow-hidden"
            >
          {/* Set up sharing + Learn the basics — always visible */}
          {showSetupCards && showWaysToSave && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="w-full grid grid-cols-2 gap-2 mb-5"
            >
              <Link
                to={SETUP()}
                className="flex items-center gap-3 px-3 py-3 rounded-2xl bg-card/80 border border-border hover:border-brand-accent/50 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-brand-600 to-brand-500 flex items-center justify-center flex-shrink-0">
                  <Share className="w-4 h-4 text-white" />
                </div>
                <p className={cn(LABEL, "text-foreground")}>Set up sharing</p>
              </Link>
              <Link
                to={BASICS()}
                className="flex items-center gap-3 px-3 py-3 rounded-2xl bg-card/80 border border-border hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-4 h-4 text-white" />
                </div>
                <p className={cn(LABEL, "text-foreground")}>Learn the basics</p>
              </Link>
            </motion.div>
          )}

          {/* Footer for setup cards */}
          {showSetupCards && showWaysToSave && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className={cn(CAPTION, "text-center -mt-2 mb-5")}
            >
              You can always find these guides in your Profile
            </motion.p>
          )}

          {/* Tabbed content */}
          {showWaysToSave && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="w-full"
            >
              {/* Tab bar */}
              <div className="relative flex rounded-xl bg-muted/60 p-1 mb-4">
                {(["new", "import"] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={cn(
                      LABEL,
                      "relative flex-1 py-2 rounded-lg transition-colors",
                      activeTab === tab ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {activeTab === tab && (
                      <motion.span
                        layoutId="emptyStateTabPill"
                        className="absolute inset-0 bg-card rounded-lg shadow-sm"
                        transition={{ type: "spring", stiffness: 400, damping: 32 }}
                      />
                    )}
                    <span className="relative">
                      {tab === "import" ? "Import old saves" : "Save something new"}
                    </span>
                  </button>
                ))}
              </div>

              {/* Tab panels — min-height matches taller tab so switching doesn't jump */}
              <div className="min-h-[272px]">
              {activeTab === "import" && (
                <motion.div
                  key="import"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.15 }}
                  className="space-y-3"
                >
                  <button
                    onClick={() => handleActionClick("images")}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-card/80 border border-border hover:border-muted-foreground/40 transition-colors text-left active:scale-[0.98]"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center flex-shrink-0">
                      <Images className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn(ROW_TITLE, "text-foreground")}>Upload screenshots</p>
                      <p className={CAPTION}>Each upload is one stash. Up to 10 images per stash.</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>
                  <Link
                    to={ACCOUNT_SETTINGS()}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-card/80 border border-border hover:border-muted-foreground/40 transition-colors text-left active:scale-[0.98]"
                  >
                    <div className="w-10 h-10 rounded-xl bg-black dark:bg-foreground flex items-center justify-center flex-shrink-0">
                      <XLogo size={20} className="text-white dark:text-background" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn(ROW_TITLE, "text-foreground")}>Import from X</p>
                      <p className={CAPTION}>Connect your X account to import bookmarks</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </Link>
                  <button
                    onClick={() => openExternalUrl(EXTENSION_LINKS.CHROME_EXTENSION)}
                    className="w-full block px-4 py-3 rounded-2xl bg-card/80 border border-border hover:border-blue-300 dark:hover:border-blue-700 transition-colors text-left active:scale-[0.98]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <svg className="w-8 h-8" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id="chrome-a" x1="3.2173" y1="15" x2="44.7812" y2="15" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#d93025"/><stop offset="1" stopColor="#ea4335"/></linearGradient>
                            <linearGradient id="chrome-b" x1="20.7219" y1="47.6791" x2="41.5039" y2="11.6837" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#fcc934"/><stop offset="1" stopColor="#fbbc04"/></linearGradient>
                            <linearGradient id="chrome-c" x1="26.5981" y1="46.5015" x2="5.8161" y2="10.506" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#1e8e3e"/><stop offset="1" stopColor="#34a853"/></linearGradient>
                          </defs>
                          <circle cx="24" cy="23.9947" r="12" fill="#fff"/>
                          <path d="M24,12H44.7812a23.9939,23.9939,0,0,0-41.5639.0029L13.6079,30l.0093-.0024A11.9852,11.9852,0,0,1,24,12Z" fill="url(#chrome-a)"/>
                          <circle cx="24" cy="24" r="9.5" fill="#1a73e8"/>
                          <path d="M34.3913,30.0029,24.0007,48A23.994,23.994,0,0,0,44.78,12.0031H23.9989l-.0025.0093A11.985,11.985,0,0,1,34.3913,30.0029Z" fill="url(#chrome-b)"/>
                          <path d="M13.6086,30.0031,3.218,12.006A23.994,23.994,0,0,0,24.0025,48L34.3931,30.0029l-.0067-.0068a11.9852,11.9852,0,0,1-20.7778.007Z" fill="url(#chrome-c)"/>
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={cn(ROW_TITLE, "text-foreground")}>Chrome import</p>
                          <span className="text-ui-badge font-medium uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground">Desktop</span>
                        </div>
                        <p className={CAPTION}>Use Chrome to bulk import existing saves from supported apps</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  </button>
                  <button
                    onClick={() => openExternalUrl(EXTENSION_LINKS.SAFARI_EXTENSION)}
                    className="w-full block px-4 py-3 rounded-2xl bg-card/80 border border-border hover:border-blue-300 dark:hover:border-blue-700 transition-colors text-left active:scale-[0.98]"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <Globe className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={cn(ROW_TITLE, "text-foreground")}>Safari import</p>
                          <span className="text-ui-badge font-medium uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground">Mac + iOS</span>
                        </div>
                        <p className={CAPTION}>Bulk import or stash from Safari on Mac, iPhone, and iPad</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  </button>
                </motion.div>
              )}

              {/* Save something new tab */}
              {activeTab === "new" && (
                <motion.div
                  key="new"
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.15 }}
                  className="space-y-3"
                >
                  <button
                    onClick={() => handleActionClick("photo")}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-card/80 border border-border hover:border-muted-foreground/40 transition-colors text-left active:scale-[0.98]"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center flex-shrink-0">
                      <Camera className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn(ROW_TITLE, "text-foreground")}>Take a photo</p>
                      <p className={CAPTION}>Snap a menu, flyer, or anything you want to remember</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>
                  <button
                    onClick={() => handleActionClick("link")}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-card/80 border border-border hover:border-muted-foreground/40 transition-colors text-left active:scale-[0.98]"
                  >
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center flex-shrink-0">
                      <Link2 className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn(ROW_TITLE, "text-foreground")}>Paste a link</p>
                      <p className={CAPTION}>Add any URL directly</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>

                  {/* Social icons — only in Save something new tab */}
                  <div className="px-4 py-4 rounded-2xl bg-card/80 border border-border">
                    <p className={cn(CAPTION, "text-center mb-3 max-w-xs mx-auto")}>
                      Open an app, find anything you want to remember later, and share it to Stasht.
                    </p>
                    <div className="flex justify-center gap-2">
                      {SOCIAL_PLATFORMS.map((platform) => (
                        <button
                          key={platform.id}
                          onClick={() => handlePlatformClick(platform)}
                          className={cn(
                            "w-10 h-10 rounded-xl flex items-center justify-center active:scale-95 transition-transform",
                            platform.bgStyle,
                            platform.iconStyle,
                            platform.id === "tiktok" && "dark:bg-foreground dark:text-background",
                            platform.id === "x" && "dark:bg-foreground dark:text-background"
                          )}
                          aria-label={`Open ${platform.name}`}
                        >
                          <div className="scale-[0.6]">{getPlatformIcon(platform.id)}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
              </div>
            </motion.div>
          )}
            </motion.div>
          )}
          </AnimatePresence>

          {mapPrompt && (
            <motion.div
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="w-full mt-6 mb-6 text-center"
            >
              <div className="flex items-center gap-3 mb-5" aria-hidden="true">
                <div className="flex-grow border-t border-border" />
                <span className={cn(CAPTION, "uppercase tracking-[0.2em]")}>or</span>
                <div className="flex-grow border-t border-border" />
              </div>
              <h3 className={cn(SECTION_TITLE, "text-foreground")}>{mapPrompt.headline}</h3>
              <Link
                to={mapPrompt.to}
                className={cn(CONTROL, "mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#1a7fd9] px-4 py-3 text-white shadow-lg shadow-[#1a7fd9]/35 transition-all hover:bg-[#166fbe] hover:shadow-xl hover:shadow-[#1a7fd9]/45 active:scale-[0.98]")}
              >
                <span>{mapPrompt.ctaLabel}</span>
                <MapPin className="w-4 h-4" />
              </Link>
            </motion.div>
          )}


          {/* Action Buttons (non-waysToSave fallback) */}
          {showActionButtons && !showWaysToSave && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="pt-6 mb-8 border-t border-border"
            >
              <p className={cn(META, "text-center mb-1")}>Have screenshots? Add them here.</p>
              <p className={cn(CAPTION, "text-center mb-4")}>You can also use the + button in the bottom right corner</p>
              <div className="flex justify-center gap-4">
                {ACTION_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      onClick={() => handleActionClick(item.key)}
                      className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
                    >
                      <div className="w-14 h-14 rounded-xl bg-card border border-border flex items-center justify-center">
                        <Icon className="w-6 h-6 text-foreground" />
                      </div>
                      <span className={CAPTION}>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Platform Buttons (non-waysToSave fallback) */}
          {showPlatformButtons && !showWaysToSave && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="pb-4 sm:pb-0 pt-6 border-t border-border"
            >
              <p className={cn(META, "text-center mb-2")}>Or jump straight in</p>
              <p className={cn(CAPTION, "text-center mb-4")}>Open an app, find a post, and share it to Stasht</p>
              <div className="flex justify-center gap-3 flex-wrap">
                {SOCIAL_PLATFORMS.map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => handlePlatformClick(platform)}
                    className={cn(
                      "w-14 h-14 rounded-2xl flex items-center justify-center active:scale-95 transition-transform",
                      platform.bgStyle,
                      platform.iconStyle,
                      platform.id === "tiktok" && "dark:bg-foreground dark:text-background",
                      platform.id === "x" && "dark:bg-foreground dark:text-background"
                    )}
                    aria-label={`Open ${platform.name}`}
                  >
                    {getPlatformIcon(platform.id)}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {(showActionButtons || showWaysToSave) && (
        <AddContentModal
          isOpen={modalOpen}
          onClose={() => { setModalOpen(false); setPendingFiles([]); }}
          contentType={modalContentType}
          initialFiles={pendingFiles.length > 0 ? pendingFiles : undefined}
        />
      )}
    </div>
  );
}
